import { useMemo, useState } from 'react';
import Split from 'react-split';
import { BulkDeleteDialog, DeleteDialogV2, ErpDataTableV2, ErpInputTableV2 } from '@/components/custom/erp';
import { getDataTableColumns, getTableDetailColumns } from './cols-definition';
import { ActionBar, FormDialog, SearchDialog } from './components';
import { PhieuThanhToanTamUngProvider } from './context';

import { useCRUD, useForms, useToast } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

function PhieuThanhToanTamUngPage() {
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const { toast } = useToast();
  const {
    addItem: addPhieuThanhToanTamUng,
    updateItem: updatePhieuThanhToanTamUng,
    deleteItem: deletePhieuThanhToanTamUng,
    refreshData: refreshPhieuThanhToanTamUngs,
    totalItems,
    currentPage,
    handlePageChange,
    data: phieuThanhToanTamUngs,
    isLoading,
    detail,
    fetchDetail,
    sumTotal
  } = useCRUD({
    endpoint: QUERY_KEYS.PHIEU_THANH_TOAN_TAM_UNG,
    sumField: 't_tt_nt'
  });
  const {
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    selectedObj,
    selectedRowIndex,
    showBulkDelete,
    handleCloseBulkDelete,
    handleRowClick,
    clearSelection,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick
  } = useForms(fetchDetail);

  const handleSearch = () => {
    setShowSearchDialog(true);
  };

  const handleSearchClose = () => {
    setShowSearchDialog(false);
  };

  const handleSearchSubmit = (filters: MuaHangRecord) => {
    console.log('Search filters:', filters);
    // TODO: Implement search functionality here
  };

  const handleSubmit = async (data: MuaHangRecord) => {
    try {
      if (formMode === 'add') {
        await addPhieuThanhToanTamUng(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updatePhieuThanhToanTamUng(selectedObj.uuid, data);
      }
      handleCloseForm();
      clearSelection();
    } catch (error) {
      toast({
        description: `Lỗi khi xử lý dữ liệu: ${error}`,
        variant: 'destructive'
      });
    }
  };
  const mainColumns = useMemo(
    () => getDataTableColumns(handleViewClick, sumTotal, isLoading),
    [handleViewClick, isLoading, sumTotal]
  );

  const tables = [
    {
      name: 'Tất cả',
      rows: phieuThanhToanTamUngs,
      columns: mainColumns
    },
    {
      name: 'Chưa ghi sổ',
      rows: phieuThanhToanTamUngs?.filter(row => row.status === '0'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#EAD1DC]' />
    },
    {
      name: 'Chờ duyệt',
      rows: phieuThanhToanTamUngs?.filter(row => row.status === '3'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-red-500' />
    },
    {
      name: 'Đã ghi sổ',
      rows: phieuThanhToanTamUngs?.filter(row => row.status === '5'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#3D85C6]' />
    }
  ];

  return (
    <div className='flex h-screen w-full flex-col overflow-hidden'>
      {showSearchDialog && (
        <SearchDialog open={showSearchDialog} onClose={handleSearchClose} onSearch={handleSearchSubmit} />
      )}

      {showForm && (
        <FormDialog
          formMode={formMode}
          open={showForm}
          onClose={handleCloseForm}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onSubmit={handleSubmit}
          onAdd={handleAddClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onCopy={handleCopyClick}
        />
      )}

      {showDelete && (
        <DeleteDialogV2
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          deleteObj={deletePhieuThanhToanTamUng}
          clearSelection={clearSelection}
        />
      )}

      {showBulkDelete && (
        <BulkDeleteDialog
          title='Phiếu thanh toán tam ứng'
          onClose={handleCloseBulkDelete}
          columns={mainColumns}
          dataRows={phieuThanhToanTamUngs}
          handleDeleteMany={uuids => {
            void Promise.all(uuids.map(uuid => deletePhieuThanhToanTamUng(uuid)));
          }}
        />
      )}
      {!showForm && (
        <>
          <ActionBar
            onAdd={handleAddClick}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onCopy={handleCopyClick}
            onSearch={handleSearch}
            onView={handleViewClick}
            onRefresh={async () => {
              await refreshPhieuThanhToanTamUngs();
              await fetchDetail(selectedObj?.uuid);
            }}
            isEditDisabled={!selectedObj}
          />

          {!isLoading && (
            <Split
              className='flex flex-1 flex-col overflow-hidden'
              direction='vertical'
              sizes={[50, 50]}
              minSize={200}
              gutterSize={4}
              gutterAlign='center'
              snapOffset={30}
              dragInterval={1}
              cursor='row-resize'
            >
              <div className='w-full overflow-hidden'>
                <ErpDataTableV2
                  tables={tables}
                  onRowClick={handleRowClick}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  selectedRowId={selectedRowIndex || undefined}
                />
              </div>

              <div className='overflow-hidden'>
                <ErpInputTableV2
                  rows={selectedObj ? detail?.chi_tiet_phieu_thanh_toan_data || [] : []}
                  columns={getTableDetailColumns}
                  showIndexColumn
                  actionsDisabled={formMode === 'view' || !selectedObj}
                  height={'100%'}
                />
              </div>
            </Split>
          )}
        </>
      )}
    </div>
  );
}

export default function PhieuThanhToanTamUngPageContent() {
  return (
    <PhieuThanhToanTamUngProvider>
      <PhieuThanhToanTamUngPage />
    </PhieuThanhToanTamUngProvider>
  );
}
